import React, { createContext, useContext, useState, useEffect } from "react";

const GeoContext = createContext();

export const useGeo = () => useContext(GeoContext);

const GEO_STORAGE_KEY = "sp_region_override";

/* Read localStorage synchronously so the FIRST render already knows the region.
   This avoids the flash where standard homepage shows briefly before switching. */
const readStoredRegion = () => {
  try {
    const v = localStorage.getItem(GEO_STORAGE_KEY);
    if (v === "pk" || v === "standard") return v;
  } catch { /* private browsing etc */ }
  return null;
};

/*
 * Check URL params for region override.
 * Supports: ?region=standard  or  ?region=pk
 * This lets ad campaigns force a specific landing experience.
 */
const readUrlRegion = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const r = params.get("region");
    if (r === "pk" || r === "standard") return r;
  } catch { /* SSR / no window */ }
  return null;
};

/*
 * Race multiple geo APIs — first one to respond with a country code wins.
 * Returns "PK" or another ISO country code, or null on failure.
 */
const detectCountryFast = async () => {
  const TIMEOUT = 2000; // 2s hard cap (was 4s with ipapi alone)

  // Strategy 1: Cloudflare /cdn-cgi/trace — fastest, plain-text, no JSON overhead
  const cfTrace = async () => {
    const res = await fetch("https://cloudflare.com/cdn-cgi/trace", {
      signal: AbortSignal.timeout(TIMEOUT),
    });
    const text = await res.text();
    const match = text.match(/loc=(\w{2})/);
    return match ? match[1] : null;
  };

  // Strategy 2: ipapi.co — reliable fallback
  const ipApi = async () => {
    const res = await fetch("https://ipapi.co/json/", {
      signal: AbortSignal.timeout(TIMEOUT),
    });
    const data = await res.json();
    return data.country_code || null;
  };

  // Race them — first valid result wins
  try {
    const country = await Promise.any([cfTrace(), ipApi()]);
    return country;
  } catch {
    return null;
  }
};

export const GeoProvider = ({ children }) => {
  // URL param takes highest priority → then localStorage → then default "standard"
  const urlRegion = readUrlRegion();
  const stored = readStoredRegion();
  const initialRegion = urlRegion || stored || "standard";

  const [region, setRegion] = useState(initialRegion);
  /*
   * KEY FIX: never block rendering.
   * - If localStorage has a region → loading = false (instant)
   * - If not → loading = false anyway, show standard immediately.
   *   When geo resolves to PK, swap in the Pakistan page.
   *   A brief standard→PK transition is far better than 3s of blank screen.
   */
  const [loading] = useState(false);

  useEffect(() => {
    // If URL param forced a region, persist it and skip IP detection
    if (urlRegion) {
      localStorage.setItem(GEO_STORAGE_KEY, urlRegion);
      return;
    }

    // If we already resolved from localStorage, skip IP check
    if (stored) return;

    const detect = async () => {
      const country = await detectCountryFast();
      if (country === "PK") {
        setRegion("pk");
        localStorage.setItem(GEO_STORAGE_KEY, "pk");
      } else if (country) {
        // Remember non-PK too so we skip detection next time
        localStorage.setItem(GEO_STORAGE_KEY, "standard");
      }
    };

    detect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchRegion = (newRegion) => {
    setRegion(newRegion);
    localStorage.setItem(GEO_STORAGE_KEY, newRegion);
  };

  return (
    <GeoContext.Provider value={{ region, switchRegion, loading, isPakistan: region === "pk" }}>
      {children}
    </GeoContext.Provider>
  );
};

export default GeoContext;
